import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../Store";

export enum SelectedTab {
  Timeline = 0,
  ActorPresets = 1,
  PropPresets = 2,
  ProjectSettings = 3,
}

export enum CollapsableColumn {
  Search = 0,
  ErrorLog = 1,
}

export type ThemeMode = "light" | "dark";

/**
 * How the browse dialogs (interaction picker, prop library, file explorer)
 * render their results. One preference for all three rather than one each —
 * the shell and every keystroke are identical between them, so a per-dialog
 * split would be a setting nobody asked for. Easy to widen into a record later
 * if that turns out to be wrong.
 */
export type BrowseDialogViewMode = "tile" | "list";
export type SequencePositionPickerType =
  | "actor"
  | "camera"
  | "prop"
  | "proximity"
  | "lookatTarget"
  | "takeLocation";
export type SequencePositionPickerPresentation = "splitView";

export interface SequencePositionPickerState {
  isOpen: boolean;
  nodeId: string;
  positionType: SequencePositionPickerType;
  title: string;
  presentation: SequencePositionPickerPresentation;
  targetIndex?: number;
}

export interface SequenceCanvasViewportState {
  x: number;
  y: number;
  zoom: number;
}

export type SequenceCanvasCommandType =
  | "formatLayout"
  | "createTextbox"
  | "createCommentZone"
  | "copySelection"
  | "cutSelection"
  | "pasteSelection"
  | "duplicateSelection";

export interface SequenceCanvasCommandState {
  type: SequenceCanvasCommandType | null;
  requestId: number;
}

export interface ViewportMapFocusState {
  x: number;
  y: number;
  requestId: number;
}

export interface SequenceCanvasSelectionState {
  nodeCount: number;
  commentZoneCount: number;
  textboxCount: number;
  cuttableCount?: number;
}

export interface QuizFeedbackPanelState {
  isOpen: boolean;
  parentQuizId: string;
}

export interface GlobalState {
  isLoading: boolean;
  isPropertiesColumnOpen: boolean;
  isMenuExpanded: boolean;
  selectedTab: SelectedTab;
  showCameraWidget: boolean;
  showEnvironmentProps: boolean;
  themeMode: ThemeMode;
  browseDialogViewMode: BrowseDialogViewMode;
  autoConnectToUnity: boolean;
  autoSave: boolean;
  isPropLibraryOpen: boolean;
  isCollapsableColumnOpen: boolean;
  selectedCollapsableColumn: CollapsableColumn;
  propLibraryTargetPosition: string;
  isQuickActorModalOpen: boolean;
  quickActorModalTargetPosition: string;
  focusedSequenceNodeId: string | null;
  sequencePositionPicker: SequencePositionPickerState;
  sequenceCanvasViewports: Record<string, SequenceCanvasViewportState>;
  shouldRestoreSequenceCanvasViewport: boolean;
  sequenceCanvasCommand: SequenceCanvasCommandState;
  viewportMapFocus: ViewportMapFocusState;
  sequenceCanvasSelection: SequenceCanvasSelectionState;
  isAdvancedSearchOpen: boolean;
  isValidationOverlayOpen: boolean;
  quizFeedback: QuizFeedbackPanelState;
  /**
   * The slide being edited in the selected PowerSlide. Shared rather than local
   * because the rail, the canvas and keyboard navigation all move it. A stale id
   * (after switching interactions, or deleting the slide) is harmless: the
   * editor falls back to the deck's first slide.
   */
  selectedSlideId: string;
}

const defaultSequencePositionPickerState: SequencePositionPickerState = {
  isOpen: false,
  nodeId: "",
  positionType: "actor",
  title: "",
  presentation: "splitView",
};

const getInitialThemeMode = (): ThemeMode => {
  const stored = localStorage.getItem("themeMode");
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  // Default to light mode, or could check system preference: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  return "light";
};

export const BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY = "browseDialogViewMode";

const getInitialBrowseDialogViewMode = (): BrowseDialogViewMode => {
  const stored = localStorage.getItem(BROWSE_DIALOG_VIEW_MODE_STORAGE_KEY);
  if (stored === "tile" || stored === "list") {
    return stored;
  }
  return "tile";
};

const getInitialAutoConnectToUnity = (): boolean => {
  return localStorage.getItem("autoConnectToUnity") === "true";
};

const getInitialAutoSave = (): boolean => {
  const stored = localStorage.getItem("autoSave");
  if (stored === "false") {
    return false;
  }
  return true;
};

const initialState: GlobalState = {
  isLoading: false,
  isPropertiesColumnOpen: true,
  isMenuExpanded: true,
  selectedTab: SelectedTab.Timeline,
  showCameraWidget: localStorage.getItem("showCameraWidget") === "true",
  showEnvironmentProps: localStorage.getItem("showEnvironmentProps") === "true",
  themeMode: getInitialThemeMode(),
  browseDialogViewMode: getInitialBrowseDialogViewMode(),
  autoConnectToUnity: getInitialAutoConnectToUnity(),
  autoSave: getInitialAutoSave(),
  isPropLibraryOpen: false,
  isCollapsableColumnOpen: false,
  selectedCollapsableColumn: CollapsableColumn.Search,
  propLibraryTargetPosition: "",
  isQuickActorModalOpen: false,
  quickActorModalTargetPosition: "",
  focusedSequenceNodeId: null,
  sequencePositionPicker: defaultSequencePositionPickerState,
  sequenceCanvasViewports: {},
  shouldRestoreSequenceCanvasViewport: false,
  sequenceCanvasCommand: {
    type: null,
    requestId: 0,
  },
  viewportMapFocus: {
    x: 0,
    y: 0,
    requestId: 0,
  },
  sequenceCanvasSelection: {
    nodeCount: 0,
    commentZoneCount: 0,
    textboxCount: 0,
    cuttableCount: 0,
  },
  isAdvancedSearchOpen: false,
  isValidationOverlayOpen: false,
  quizFeedback: {
    isOpen: false,
    parentQuizId: "",
  },
  selectedSlideId: "",
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsPropertiesColumnOpen: (state, action: PayloadAction<boolean>) => {
      state.isPropertiesColumnOpen = action.payload;
    },
    setIsMenuExpanded: (state, action: PayloadAction<boolean>) => {
      state.isMenuExpanded = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<SelectedTab>) => {
      state.selectedTab = action.payload;
      if (action.payload !== SelectedTab.Timeline) {
        state.sequencePositionPicker = defaultSequencePositionPickerState;
      }
    },
    setShowCameraWidget: (state, action: PayloadAction<boolean>) => {
      state.showCameraWidget = action.payload;
    },
    setShowEnvironmentProps: (state, action: PayloadAction<boolean>) => {
      state.showEnvironmentProps = action.payload;
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
    },
    setBrowseDialogViewMode: (
      state,
      action: PayloadAction<BrowseDialogViewMode>
    ) => {
      state.browseDialogViewMode = action.payload;
    },
    setAutoConnectToUnity: (state, action: PayloadAction<boolean>) => {
      state.autoConnectToUnity = action.payload;
    },
    setAutoSave: (state, action: PayloadAction<boolean>) => {
      state.autoSave = action.payload;
    },
    applyUserPreferences: (
      state,
      action: PayloadAction<{ darkMode?: boolean; autoSave?: boolean }>
    ) => {
      if (typeof action.payload.darkMode === "boolean") {
        state.themeMode = action.payload.darkMode ? "dark" : "light";
      }
      if (typeof action.payload.autoSave === "boolean") {
        state.autoSave = action.payload.autoSave;
      }
    },
    setIsPropLibraryOpen: (
      state,
      action: PayloadAction<{ open: boolean; positionId: string }>
    ) => {
      state.isPropLibraryOpen = action.payload.open;
      state.propLibraryTargetPosition = action.payload.positionId;
    },
    setIsCollapsableColumnOpen: (
      state,
      action: PayloadAction<{ open: boolean; column: CollapsableColumn }>
    ) => {
      state.isCollapsableColumnOpen = action.payload.open;
      state.selectedCollapsableColumn = action.payload.column;
    },
    setPropLibraryTargetPosition: (state, action: PayloadAction<string>) => {
      state.propLibraryTargetPosition = action.payload;
    },
    setIsQuickActorModalOpen: (
      state,
      action: PayloadAction<{ open: boolean; positionId: string }>
    ) => {
      state.isQuickActorModalOpen = action.payload.open;
      state.quickActorModalTargetPosition = action.payload.positionId;
    },
    setFocusedSequenceNodeId: (state, action: PayloadAction<string | null>) => {
      state.focusedSequenceNodeId = action.payload;
    },
    openSequencePositionPicker: (
      state,
      action: PayloadAction<{
        nodeId: string;
        positionType: SequencePositionPickerType;
        title: string;
        presentation?: SequencePositionPickerPresentation;
        targetIndex?: number;
      }>
    ) => {
      state.sequencePositionPicker = {
        isOpen: true,
        nodeId: action.payload.nodeId,
        positionType: action.payload.positionType,
        title: action.payload.title,
        presentation: action.payload.presentation ?? "splitView",
        targetIndex: action.payload.targetIndex,
      };
      state.shouldRestoreSequenceCanvasViewport = false;
    },
    closeSequencePositionPicker: (state) => {
      state.sequencePositionPicker = defaultSequencePositionPickerState;
      state.shouldRestoreSequenceCanvasViewport = true;
    },
    setSequenceCanvasViewport: (
      state,
      action: PayloadAction<{
        sequenceId: string;
        viewport: SequenceCanvasViewportState;
      }>
    ) => {
      state.sequenceCanvasViewports[action.payload.sequenceId] =
        action.payload.viewport;
    },
    clearSequenceCanvasViewportRestore: (state) => {
      state.shouldRestoreSequenceCanvasViewport = false;
    },
    queueSequenceCanvasCommand: (
      state,
      action: PayloadAction<SequenceCanvasCommandType>
    ) => {
      state.sequenceCanvasCommand = {
        type: action.payload,
        requestId: state.sequenceCanvasCommand.requestId + 1,
      };
    },
    clearSequenceCanvasCommand: (state) => {
      state.sequenceCanvasCommand = {
        ...state.sequenceCanvasCommand,
        type: null,
      };
    },
    requestViewportMapFocus: (
      state,
      action: PayloadAction<{ x: number; y: number }>
    ) => {
      state.viewportMapFocus = {
        x: action.payload.x,
        y: action.payload.y,
        requestId: state.viewportMapFocus.requestId + 1,
      };
    },
    setSequenceCanvasSelection: (
      state,
      action: PayloadAction<SequenceCanvasSelectionState>
    ) => {
      state.sequenceCanvasSelection = action.payload;
    },
    setIsAdvancedSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.isAdvancedSearchOpen = action.payload;
      if (action.payload) state.isValidationOverlayOpen = false;
    },
    setIsValidationOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isValidationOverlayOpen = action.payload;
      if (action.payload) state.isAdvancedSearchOpen = false;
    },
    openQuizFeedback: (state, action: PayloadAction<string>) => {
      state.quizFeedback = {
        isOpen: true,
        parentQuizId: action.payload,
      };
    },
    closeQuizFeedback: (state) => {
      state.quizFeedback = {
        isOpen: false,
        parentQuizId: "",
      };
    },
    setSelectedSlideId: (state, action: PayloadAction<string>) => {
      state.selectedSlideId = action.payload;
    },
  },
});

// reducers
export const {
  setIsLoading,
  setIsPropertiesColumnOpen,
  setIsMenuExpanded,
  setSelectedTab,
  setShowCameraWidget,
  setShowEnvironmentProps,
  setThemeMode,
  setBrowseDialogViewMode,
  setAutoConnectToUnity,
  setAutoSave,
  applyUserPreferences,
  setIsPropLibraryOpen,
  setIsCollapsableColumnOpen,
  setPropLibraryTargetPosition,
  setIsQuickActorModalOpen,
  setFocusedSequenceNodeId,
  openSequencePositionPicker,
  closeSequencePositionPicker,
  setSequenceCanvasViewport,
  clearSequenceCanvasViewportRestore,
  queueSequenceCanvasCommand,
  clearSequenceCanvasCommand,
  requestViewportMapFocus,
  setSequenceCanvasSelection,
  setIsAdvancedSearchOpen,
  setIsValidationOverlayOpen,
  openQuizFeedback,
  closeQuizFeedback,
  setSelectedSlideId,
} = globalSlice.actions;

export const selectIsLoading = (state: RootState) => state.global.isLoading;
export const selectIsPropertiesColumnOpen = (state: RootState) =>
  state.global.isPropertiesColumnOpen;
export const selectIsMenuExpanded = (state: RootState) =>
  state.global.isMenuExpanded;
export const selectSelectedTab = (state: RootState) => state.global.selectedTab;
export const selectShowCameraWidget = (state: RootState) =>
  state.global.showCameraWidget;
export const selectShowEnvironmentProps = (state: RootState) =>
  state.global.showEnvironmentProps;
export const selectThemeMode = (state: RootState) => state.global.themeMode;
export const selectBrowseDialogViewMode = (state: RootState) =>
  state.global.browseDialogViewMode ?? "tile";
export const selectAutoConnectToUnity = (state: RootState) =>
  state.global.autoConnectToUnity;
export const selectAutoSave = (state: RootState) => state.global.autoSave;
export const selectIsPropLibraryOpen = (state: RootState) =>
  state.global.isPropLibraryOpen;
export const selectIsCollapsableColumnOpen = (state: RootState) =>
  state.global.isCollapsableColumnOpen;
export const selectSelectedCollapsableColumn = (state: RootState) =>
  state.global.selectedCollapsableColumn;
export const selectPropLibraryTargetPosition = (state: RootState) =>
  state.global.propLibraryTargetPosition;
export const selectIsQuickActorModalOpen = (state: RootState) =>
  state.global.isQuickActorModalOpen;
export const selectQuickActorModalTargetPosition = (state: RootState) =>
  state.global.quickActorModalTargetPosition;
export const selectFocusedSequenceNodeId = (state: RootState) =>
  state.global.focusedSequenceNodeId;
export const selectSequencePositionPicker = (state: RootState) =>
  state.global.sequencePositionPicker;
export const selectIsSequencePositionPickerOpen = (state: RootState) =>
  state.global.sequencePositionPicker.isOpen;
export const selectSequenceCanvasViewport = (
  state: RootState,
  sequenceId: string,
) => state.global.sequenceCanvasViewports[sequenceId] ?? null;
export const selectShouldRestoreSequenceCanvasViewport = (state: RootState) =>
  state.global.shouldRestoreSequenceCanvasViewport;
export const selectSequenceCanvasCommand = (state: RootState) =>
  state.global.sequenceCanvasCommand;
export const selectViewportMapFocus = (state: RootState) =>
  state.global.viewportMapFocus;
export const selectSequenceCanvasSelection = (state: RootState) =>
  state.global.sequenceCanvasSelection;
export const selectIsAdvancedSearchOpen = (state: RootState) =>
  state.global.isAdvancedSearchOpen;
export const selectIsValidationOverlayOpen = (state: RootState) =>
  state.global.isValidationOverlayOpen;
export const selectIsQuizFeedbackOpen = (state: RootState) =>
  state.global.quizFeedback?.isOpen ?? false;
export const selectQuizFeedbackParentQuizId = (state: RootState) =>
  state.global.quizFeedback?.parentQuizId ?? "";
export const selectSelectedSlideId = (state: RootState) =>
  state.global.selectedSlideId ?? "";

export default globalSlice.reducer;
